import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  BankRecord,
  CashboxRecord,
  FinanceApiService,
  FinancialAccountRecord
} from './finance-api.service';
import {
  PostingFilter,
  SourceTypeFilter,
  matchesAmountRange,
  matchesPostingFilter,
  matchesText,
  sumAmount
} from './finance-payments-report.utils';

export interface BankCashboxReportRow {
  id: string;
  sourceType: 'Bank' | 'Cashbox';
  displayName: string;
  identifier: string;
  amount: number;
  currencyCode: string;
  journalEntryId?: string;
}

@Component({
  standalone: true,
  selector: 'ain-finance-payments-report-bank-cashbox',
  imports: [AinPageComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule, DecimalPipe],
  template: `
    <ain-page [title]="t('finance.reports.bankCashbox.title')" [description]="t('finance.reports.bankCashbox.description')">
      <section class="card list-card">
        <div class="filters">
          <p-select
            [options]="sourceOptions"
            [(ngModel)]="sourceFilter"
            optionLabel="label"
            optionValue="value"
            [placeholder]="t('finance.reports.filters.sourceType')" />
          <input pInputText type="text" [(ngModel)]="searchName" [placeholder]="t('finance.reports.filters.name')" />
          <input pInputText type="text" [(ngModel)]="searchIdentifier" [placeholder]="t('finance.reports.filters.identifier')" />
          <input pInputText type="text" [(ngModel)]="searchCurrency" [placeholder]="t('finance.reports.filters.currency')" />
          <input pInputText type="number" [(ngModel)]="minAmount" [placeholder]="t('finance.reports.filters.minAmount')" />
          <input pInputText type="number" [(ngModel)]="maxAmount" [placeholder]="t('finance.reports.filters.maxAmount')" />
          <p-select
            [options]="postingOptions"
            [(ngModel)]="postingFilter"
            optionLabel="label"
            optionValue="value"
            [placeholder]="t('finance.reports.filters.postingStatus')" />
          <button pButton type="button" [label]="t('finance.reports.apply')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('finance.reports.clear')" severity="secondary" (click)="clearFilters()"></button>
          <button pButton type="button" [label]="t('finance.reports.refresh')" severity="secondary" (click)="load()"></button>
        </div>

        <p-table [value]="filteredRows()" [paginator]="true" [rows]="15" [rowsPerPageOptions]="[15, 25, 50]">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.reports.fields.sourceType') }}</th>
              <th>{{ t('finance.reports.fields.name') }}</th>
              <th>{{ t('finance.reports.fields.identifier') }}</th>
              <th>{{ t('finance.reports.fields.amount') }}</th>
              <th>{{ t('finance.reports.fields.currency') }}</th>
              <th>{{ t('finance.reports.fields.postingStatus') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ sourceTypeLabel(row.sourceType) }}</td>
              <td><button type="button" class="link-btn" (click)="openDetail(row)">{{ row.displayName }}</button></td>
              <td>{{ row.identifier || '-' }}</td>
              <td>{{ row.amount | number:'1.0-0' }}</td>
              <td>{{ row.currencyCode }}</td>
              <td>{{ postingLabel(row) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6">{{ t('finance.reports.noRecords') }}</td></tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr class="totals-row">
              <td colspan="3"><strong>{{ t('finance.reports.totals.count') }}: {{ filteredRows().length }} — {{ t('finance.reports.totals.amount') }}</strong></td>
              <td colspan="3"><strong>{{ totalAmount() | number:'1.0-0' }}</strong></td>
            </tr>
          </ng-template>
        </p-table>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters p-select { min-width: 160px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .totals-row td { background: #f9fafb; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
  `]
})
export class FinancePaymentsReportBankCashboxComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly rows = signal<BankCashboxReportRow[]>([]);
  readonly filteredRows = signal<BankCashboxReportRow[]>([]);
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly errorMessage = signal('');
  readonly totalAmount = computed(() => sumAmount(this.filteredRows()));

  sourceFilter: SourceTypeFilter = 'all';
  searchName = '';
  searchIdentifier = '';
  searchCurrency = '';
  minAmount: number | null = null;
  maxAmount: number | null = null;
  postingFilter: PostingFilter = 'all';

  readonly sourceOptions = [
    { value: 'all' as const, label: '' },
    { value: 'bank' as const, label: '' },
    { value: 'cashbox' as const, label: '' }
  ];

  readonly postingOptions = [
    { value: 'all' as const, label: '' },
    { value: 'posted' as const, label: '' },
    { value: 'unposted' as const, label: '' }
  ];

  ngOnInit(): void {
    this.sourceOptions[0].label = this.t('finance.reports.filters.sourceAll');
    this.sourceOptions[1].label = this.t('finance.reports.filters.sourceBank');
    this.sourceOptions[2].label = this.t('finance.reports.filters.sourceCashbox');
    this.postingOptions[0].label = this.t('finance.reports.filters.postingAll');
    this.postingOptions[1].label = this.t('finance.reports.filters.postingPosted');
    this.postingOptions[2].label = this.t('finance.reports.filters.postingUnposted');
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    forkJoin({
      accounts: this.api.listFinancialAccounts().pipe(catchError(() => of([]))),
      banks: this.api.listAccountingBanks(undefined, undefined, { page: 1, pageSize: 500 }).pipe(catchError(() => of([]))),
      cashboxes: this.api.listAccountingCashboxes(undefined, undefined, { page: 1, pageSize: 500 }).pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ accounts, banks, cashboxes }) => {
        this.accounts.set(accounts);
        const rows: BankCashboxReportRow[] = [
          ...banks.map(item => this.mapBank(item)),
          ...cashboxes.map(item => this.mapCashbox(item))
        ];
        this.rows.set(rows);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.errorMessage.set(this.t('finance.reports.bankCashbox.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const filtered = this.rows().filter(row => {
      if (this.sourceFilter === 'bank' && row.sourceType !== 'Bank') {
        return false;
      }
      if (this.sourceFilter === 'cashbox' && row.sourceType !== 'Cashbox') {
        return false;
      }
      if (!matchesText(row.displayName, this.searchName)) {
        return false;
      }
      if (!matchesText(row.identifier, this.searchIdentifier)) {
        return false;
      }
      if (!matchesText(row.currencyCode, this.searchCurrency)) {
        return false;
      }
      if (!matchesAmountRange(row.amount, this.minAmount, this.maxAmount)) {
        return false;
      }
      if (!matchesPostingFilter(row.journalEntryId, this.postingFilter)) {
        return false;
      }
      return true;
    });

    this.filteredRows.set(filtered);
  }

  clearFilters(): void {
    this.sourceFilter = 'all';
    this.searchName = '';
    this.searchIdentifier = '';
    this.searchCurrency = '';
    this.minAmount = null;
    this.maxAmount = null;
    this.postingFilter = 'all';
    this.applyFilters();
  }

  openDetail(row: BankCashboxReportRow): void {
    if (row.sourceType === 'Bank') {
      void this.router.navigate(['/financial/payments/banks', row.id]);
      return;
    }
    void this.router.navigate(['/financial/payments/cashboxes', row.id]);
  }

  sourceTypeLabel(sourceType: BankCashboxReportRow['sourceType']): string {
    return sourceType === 'Bank'
      ? this.t('finance.reports.filters.sourceBank')
      : this.t('finance.reports.filters.sourceCashbox');
  }

  postingLabel(row: BankCashboxReportRow): string {
    return row.journalEntryId
      ? this.t('finance.reports.posting.posted')
      : this.t('finance.reports.posting.unposted');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private mapBank(bank: BankRecord): BankCashboxReportRow {
    return {
      id: bank.id,
      sourceType: 'Bank',
      displayName: bank.bankName || bank.number,
      identifier: bank.accountNumber || bank.iban || '-',
      amount: bank.amount,
      currencyCode: bank.currencyCode,
      journalEntryId: bank.journalEntryId
    };
  }

  private mapCashbox(cashbox: CashboxRecord): BankCashboxReportRow {
    const account = this.accounts().find(item => item.id.toLowerCase() === cashbox.accountingAccountId.toLowerCase());
    return {
      id: cashbox.id,
      sourceType: 'Cashbox',
      displayName: cashbox.name || cashbox.number,
      identifier: account ? account.code : cashbox.accountingAccountId || '-',
      amount: cashbox.amount,
      currencyCode: cashbox.currencyCode,
      journalEntryId: cashbox.journalEntryId
    };
  }
}
